import CallAudioPlayer from 'components/organisms/call-audio-player';
import DraggableModal from 'components/organisms/draggableModal';
import { useGetCallTranscribe } from 'infrastructure/hooks/calls/use-get-call-transcribe';
import { useGetCallRecordLink } from 'infrastructure/hooks/ccm';
import { useReducer, useState } from 'react';
import { DateFormats } from 'infrastructure/enums';
import { DateFormatter } from 'infrastructure/functions';
import ClockIcon from 'components/atoms/icons/clock.svg?react';
import PhoneIcon from 'components/atoms/icons/phone.svg?react';

import s from './styles.module.scss';
import ReadingThresholdValues from './reading-threshold-values';
import Time from './time';
import LinkedReadingsModal from './linked-readings-modal';

import type {
  ReadingStatus,
  ReadingType,
  ModuleType,
} from 'infrastructure/enums';
import type {
  ILinkedCallReadings,
  TStatuses,
  TThresholdName,
  Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { FC, ReactNode } from 'react';
import type { IPatientThresholds } from 'infrastructure/interfaces';

interface IChildrenProps {
  loading: boolean;
  disable: boolean;
  handleDownload: () => void;
}

type BaseProps = {
  callId: string;
  phone?: string;
  title: string;
  patientGuid: string;
  module: ModuleType.RPM;
  createdAt: string;
  children: (props: IChildrenProps) => ReactNode;
  linkedCallReadings?: ILinkedCallReadings[];
  patientTimezone: string;
  thresholds: IPatientThresholds;
  withLinkedReadings?: boolean;
};

type ReadingCallAudioPlayerProps =
  | (BaseProps & {
      withThresholds: false;
    })
  | (BaseProps & {
      withThresholds: true;
      globalStatus: ReadingStatus;
      values: Value;
      statuses: TStatuses;
      readingType: ReadingType;
      thresholdName?: TThresholdName | null;
    });

const ReadingCallAudioPlayer: FC<ReadingCallAudioPlayerProps> = (props) => {
  const {
    children,
    callId,
    phone,
    title,
    patientGuid,
    module,
    createdAt,
    withThresholds,
    linkedCallReadings,
    patientTimezone,
    thresholds,
    withLinkedReadings = true,
  } = props;

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [showTranscript, toggleShowTranscript] = useReducer((v) => !v, false);
  const [openLinkedCallReadings, toggleOpenLinkedCallReadings] = useReducer(
    (v) => !v,
    false,
  );

  const { getCallRecordLink, getCallRecordLinkLoading } =
    useGetCallRecordLink();

  const handleDownload = async () => {
    const url = await getCallRecordLink({
      callId,
      patientGuid,
      module,
    });
    if (url) {
      setAudioUrl(url);
    }
  };

  const { getCallTranscribeLoading, transcripts, callTranscribeErrorMessage } =
    useGetCallTranscribe({
      callId,
      patientGuid,
      module,
      enabled: showTranscript,
    });

  const subTitle = (
    <div className={s['modal-subtitle']}>
      <ClockIcon />
      {DateFormatter({
        date: createdAt,
        format: DateFormats['MM-dd-yy | h:mm:ss a'],
      })}
      {phone && (
        <>
          <PhoneIcon /> {phone}
        </>
      )}
    </div>
  );

  const subHeader = () => {
    if (withThresholds) {
      const { globalStatus, values, statuses, readingType, thresholdName } =
        props;

      return (
        <ReadingThresholdValues
          globalStatus={globalStatus}
          values={values}
          statuses={statuses}
          thresholds={thresholds}
          readingType={readingType}
          thresholdName={thresholdName}
        />
      );
    }
    return undefined;
  };

  return (
    <>
      {audioUrl && (
        <DraggableModal
          data-expanded={showTranscript}
          title={title}
          subTitleClassName={s['modal-subtitle']}
          subTitle={subTitle}
          isOpen
          toggle={() => {
            setAudioUrl(null);
          }}
          modalClassName={s.modal}
          bodyClassName={s['modal-body']}
          footerClassName={s['modal-footer']}
          subHeader={subHeader()}
        >
          <CallAudioPlayer
            audioUrl={audioUrl}
            callId={callId}
            transcripts={transcripts}
            transcriptsLoading={getCallTranscribeLoading}
            showTranscript={showTranscript}
            toggleShowTranscript={toggleShowTranscript}
            callTranscribeErrorMessage={callTranscribeErrorMessage}
            playerContainerClassName={s['player-container']}
            timeComponent={({ currentTimeSeconds, durationSeconds }) => (
              <Time
                currentTimeSeconds={currentTimeSeconds}
                durationSeconds={durationSeconds}
                callId={callId}
                onClick={toggleOpenLinkedCallReadings}
                linkedCallReadingsCount={linkedCallReadings?.length}
                showLinkedReadingsBtn={withLinkedReadings}
              />
            )}
          />
        </DraggableModal>
      )}
      {children({
        loading: getCallRecordLinkLoading,
        disable: !!audioUrl,
        handleDownload,
      })}
      <LinkedReadingsModal
        linkedCallReadings={linkedCallReadings ?? []}
        open={openLinkedCallReadings}
        toggle={toggleOpenLinkedCallReadings}
        thresholds={thresholds}
        patientTimezone={patientTimezone}
      />
    </>
  );
};
export default ReadingCallAudioPlayer;
